import { memo, useCallback } from 'react';
import { MinusIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

interface CartQuantitySelectorProps {
  quantity: number;
  onIncrease: () => void;
  onDecrease: () => void;
  onRemove: () => void;
  isLoading?: boolean;
  max?: number;
  size?: 'sm' | 'md';
}

// Button class constants to avoid recreation
const BUTTON_CLASSES = {
  sm: 'p-1 rounded border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors',
  md: 'p-1.5 rounded-md border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors',
} as const;

const ICON_CLASSES = { sm: 'h-3.5 w-3.5', md: 'h-4 w-4' } as const;
const TEXT_CLASSES = { sm: 'w-6 text-center text-sm', md: 'w-8 text-center text-sm font-medium' } as const;

/**
 * CartQuantitySelector (memoized)
 *
 * A reusable quantity selector for cart items.
 * Shows trash icon instead of minus when quantity is 1.
 */
export const CartQuantitySelector = memo(function CartQuantitySelector({
  quantity,
  onIncrease,
  onDecrease,
  onRemove,
  isLoading = false,
  max,
  size = 'md',
}: CartQuantitySelectorProps) {
  const isMinQuantity = quantity <= 1;
  const isMaxQuantity = max !== undefined && quantity >= max;

  const buttonClasses = BUTTON_CLASSES[size];
  const iconClasses = ICON_CLASSES[size];
  const textClasses = TEXT_CLASSES[size];

  // Memoized decrease handler
  const handleDecrease = useCallback(() => {
    if (isMinQuantity) {
      onRemove();
    } else {
      onDecrease();
    }
  }, [isMinQuantity, onRemove, onDecrease]);

  return (
    <div className="flex items-center gap-1">
      {/* Decrease/Remove button */}
      <button
        type="button"
        onClick={handleDecrease}
        disabled={isLoading}
        className={`${buttonClasses} ${isMinQuantity ? 'text-red-500 hover:text-red-600 hover:bg-red-50 border-red-200' : 'text-gray-600'}`}
        aria-label={isMinQuantity ? 'Eliminar' : 'Disminuir cantidad'}
      >
        {isMinQuantity ? (
          <TrashIcon className={iconClasses} />
        ) : (
          <MinusIcon className={iconClasses} />
        )}
      </button>

      {/* Quantity display */}
      <span className={`${textClasses} ${isLoading ? 'opacity-50' : ''}`}>
        {quantity}
      </span>

      {/* Increase button */}
      <button
        type="button"
        onClick={onIncrease}
        disabled={isLoading || isMaxQuantity}
        className={buttonClasses}
        aria-label="Aumentar cantidad"
      >
        <PlusIcon className={`${iconClasses} text-gray-600`} />
      </button>
    </div>
  );
});
